import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Plus } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import {
  supplierAdministrationKeys,
  useGetSupplierUsers,
  useCreateSupplierUser,
} from "../../../../hooks/query/supplier-administration";
import { useGetUsersRoles } from "../../../../hooks/query/user-administration";
import { constructQueryParamsString, showErrorToast, showSuccessToast } from "../../../../utils";
import LocalSpinner from "../../../../components/LocalSpinner";
import useAirUserRoles from "../../../../utils/AuthUtils";
import SupplierUserModal from "../../../SupplierManagement/components/SupplierUsers/SupplierUserModal";

const STATUS_CONFIG = {
  ACTIVE: { label: "Active", bg: "green.100", color: "green.800" },
  DEACTIVATED: { label: "Deactivated", bg: "red.100", color: "red.800" },
  PENDING: { label: "Pending", bg: "orange.100", color: "orange.800" },
};

const UserStatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status ?? "Active", bg: "green.100", color: "green.800" };
  return (
    <Badge bg={cfg.bg} color={cfg.color} borderRadius="full" px={2.5} py={0.5} fontSize="11px" fontWeight="600" textTransform="none">
      {cfg.label}
    </Badge>
  );
};

// GET /administration/supplier/users?supplier=<name> — returns only UserType.SUPPLIER users
// Response shape: { page: { content: [{ id, username, firstName, lastName, status, supplierId, supplierName }] } }
function SupplierUsers({ supplier }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isESGAdmin, isOrgAdmin } = useAirUserRoles();
  const canAddUser = isESGAdmin || isOrgAdmin;

  const { isOpen: isModalOpen, onOpen: openModal, onClose: closeModal } = useDisclosure();
  const [modalData, setModalData] = useState(undefined);

  const paramsString = constructQueryParamsString({
    supplier: supplier.name,
    page: 0,
    size: 200,
  });

  const { data, isFetching, refetch } = useGetSupplierUsers(paramsString, {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const { data: usersRoles } = useGetUsersRoles({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const { mutate: createUser, isLoading: creating } = useCreateSupplierUser({
    onSuccess: () => {
      showSuccessToast(toast, "User invited successfully");
      closeModal();
      refetch();
      queryClient.invalidateQueries(supplierAdministrationKeys.all());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const handleOpenAdd = () => {
    // Pre-populate supplierId (DB Long id) so the supplier dropdown is pre-selected
    setModalData(supplier.id ? { supplierId: String(supplier.id) } : undefined);
    openModal();
  };

  const handleSubmit = (_id, data) => {
    createUser(data);
  };

  // Response is { page: { content: [...] } }
  const users = data?.page?.content ?? [];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">Users</Text>
        {canAddUser && (
          <Button
            bg="limeShade.600"
            color="white"
            borderRadius="full"
            _hover={{ bg: "limeShade.700" }}
            size="sm"
            leftIcon={<Plus size={14} weight="bold" />}
            isLoading={creating}
            onClick={handleOpenAdd}
          >
            Add user
          </Button>
        )}
      </Flex>

      {isFetching ? (
        <Box mt={4}><LocalSpinner /></Box>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Name</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Username</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.length === 0 ? (
                <Tr>
                  <Td colSpan={3} py={6} px={4} textAlign="center">
                    <Text fontSize="14px" color="gray.400">No users found for this supplier.</Text>
                  </Td>
                </Tr>
              ) : users.map((u, idx) => (
                <Tr key={u.id} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100" _hover={{ bg: "gray.50" }}>
                  <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="600">
                    {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                  </Td>
                  <Td py={3} px={4} fontSize="14px" color="gray.700">{u.username}</Td>
                  <Td py={3} px={4}><UserStatusBadge status={u.status} /></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {isModalOpen && usersRoles && (
        <SupplierUserModal
          modalData={modalData}
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmit}
          roles={usersRoles.roles}
        />
      )}
    </Box>
  );
}

export default SupplierUsers;
